export interface PCMChunk {
  pcmBase64: string;
  level: number;
  bars: number[];
}

export type PCMListener = (payload: PCMChunk) => void;

const TARGET_SAMPLE_RATE = 16000;
const CHUNK_SAMPLES = 1600;

// Exported so Pipeline B's meter can attach the SAME worklet to the microphone stream
// it already negotiated, instead of opening a second capture (owner ruling 2026-07-29,
// blocker 3). Export only — startMic/stopMic and every Pipeline A path are unchanged.
export const WORKLET_SRC = `
class RexyPCMWorklet extends AudioWorkletProcessor {
  constructor() {
    super();
    this.sourceRate = sampleRate;
    this.targetRate = ${TARGET_SAMPLE_RATE};
    this.chunkSamples = ${CHUNK_SAMPLES};
    this.pending = [];
    this.readPos = 0;
  }

  process(inputs) {
    const input = inputs[0] && inputs[0][0];
    if (!input || input.length === 0) return true;

    const ratio = this.sourceRate / this.targetRate;
    while (this.readPos < input.length) {
      const i0 = Math.floor(this.readPos);
      const i1 = Math.min(i0 + 1, input.length - 1);
      const frac = this.readPos - i0;
      const sample = input[i0] + (input[i1] - input[i0]) * frac;
      this.pending.push(Math.max(-1, Math.min(1, sample)));
      this.readPos += ratio;

      if (this.pending.length === this.chunkSamples) {
        this.flushChunk();
      }
    }
    this.readPos -= input.length;
    return true;
  }

  flushChunk() {
    const samples = this.pending;
    this.pending = [];

    let sumSq = 0;
    const pcm = new ArrayBuffer(samples.length * 2);
    const view = new DataView(pcm);
    for (let i = 0; i < samples.length; i++) {
      const s = samples[i];
      sumSq += s * s;
      const v = s < 0 ? s * 0x8000 : s * 0x7fff;
      view.setInt16(i * 2, Math.max(-32768, Math.min(32767, Math.round(v))), true);
    }

    const rms = Math.sqrt(sumSq / samples.length);
    const level = Math.min(rms / 0.25, 1);
    const bars = [];
    const barCount = 16;
    const seg = Math.max(1, Math.floor(samples.length / barCount));
    for (let b = 0; b < barCount; b++) {
      let s = 0;
      let n = 0;
      const start = b * seg;
      const end = b === barCount - 1 ? samples.length : Math.min(samples.length, start + seg);
      for (let i = start; i < end; i++) {
        s += samples[i] * samples[i];
        n++;
      }
      bars.push(n ? Math.min(Math.sqrt(s / n) / 0.25, 1) : 0);
    }

    this.port.postMessage({ pcmBuffer: pcm, level, bars }, [pcm]);
  }
}

registerProcessor('rexy-pcm-worklet', RexyPCMWorklet);
`;

let active = false;
let currentListener: PCMListener | null = null;
let stream: MediaStream | null = null;
let ctx: AudioContext | null = null;
let source: MediaStreamAudioSourceNode | null = null;
let node: AudioWorkletNode | null = null;
let workletUrl: string | null = null;

function encodeBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export async function startMic(onChunk: PCMListener): Promise<void> {
  if (active) return;
  currentListener = onChunk;

  const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
  if (!AudioContextCtor) throw new Error('audio_context_unavailable');

  const nextStream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
    },
    video: false,
  });

  try {
    const nextCtx: AudioContext = new AudioContextCtor();
    if (nextCtx.state === 'suspended') {
      await nextCtx.resume().catch((e) => {
        // Don't throw — let the worklet attach anyway; some browsers transition
        // 'suspended' → 'running' on the first sample. But surface the rejection
        // so a future "mic dead" can be diagnosed without re-instrumenting.
        console.warn('[audioStream] AudioContext.resume() rejected:', e,
          '— ctx.state now:', nextCtx.state);
      });
    }
    const blob = new Blob([WORKLET_SRC], { type: 'application/javascript' });
    const nextWorkletUrl = URL.createObjectURL(blob);
    await nextCtx.audioWorklet.addModule(nextWorkletUrl);

    const nextSource = nextCtx.createMediaStreamSource(nextStream);
    const nextNode = new AudioWorkletNode(nextCtx, 'rexy-pcm-worklet');
    nextNode.port.onmessage = (event: MessageEvent) => {
      if (!currentListener) return;
      const data = event.data as { pcmBuffer?: ArrayBuffer; level?: number; bars?: number[] };
      if (!data?.pcmBuffer) return;
      currentListener({
        pcmBase64: encodeBase64(data.pcmBuffer),
        level: Math.max(0, Math.min(1, Number(data.level ?? 0))),
        bars: Array.isArray(data.bars) ? data.bars.map((v) => Math.max(0, Math.min(1, Number(v) || 0))) : [],
      });
    };

    nextSource.connect(nextNode);

    stream = nextStream;
    ctx = nextCtx;
    source = nextSource;
    node = nextNode;
    workletUrl = nextWorkletUrl;
    active = true;
  } catch (e) {
    nextStream.getTracks().forEach((track) => track.stop());
    currentListener = null;
    throw e;
  }
}

export function stopMic(): void {
  if (!active && !currentListener && !stream && !ctx) return;
  active = false;
  currentListener = null;
  try { node?.port.close(); } catch {}
  try { node?.disconnect(); } catch {}
  try { source?.disconnect(); } catch {}
  stream?.getTracks().forEach((track) => track.stop());
  if (ctx) void ctx.close().catch(() => {});
  if (workletUrl) URL.revokeObjectURL(workletUrl);
  node = null;
  source = null;
  stream = null;
  ctx = null;
  workletUrl = null;
}

export function isMicActive(): boolean {
  return active;
}

export function restartMic(): void {
  const listener = currentListener;
  if (!listener) return;
  stopMic();
  void startMic(listener).catch(() => {});
}
